import type { FlowStep } from '../types';
import { FLOW_STEPS } from '../types';

// =======================
// Initial Step Resolution
// =======================

export type GetInitialStepParams = {
  isLoggedIn: boolean;
  showLoyaltyEnrollment: boolean;
};

/**
 * Determines the initial step for the event signup flow based on user state.
 *
 * @param params.isLoggedIn - Whether the user is currently logged in
 * @param params.showLoyaltyEnrollment - Whether to show the loyalty enrollment step
 * @returns The appropriate initial flow step
 */
export function getInitialStep(params: GetInitialStepParams): FlowStep {
  const { isLoggedIn, showLoyaltyEnrollment } = params;

  if (!isLoggedIn) {
    return FLOW_STEPS.EVENT_SIGNUP_START;
  }

  if (showLoyaltyEnrollment) {
    return FLOW_STEPS.EVENT_LOYALTY_ENROLL;
  }

  return FLOW_STEPS.EVENT_SIGNUP_END;
}

// ====================
// Next Step Resolution
// ====================

export type GetNextStepParams = {
  currentStep: FlowStep;
  showLoyaltyEnrollment: boolean;
};

/**
 * Determines the next step in the IRL event signup flow based on current step and user state.
 *
 * @param params.currentStep - The current flow step
 * @param params.showLoyaltyEnrollment - Whether to show the loyalty enrollment step
 * @returns The next flow step, or null if at terminal state
 */
export function getNextStep(params: GetNextStepParams): FlowStep | null {
  const { currentStep, showLoyaltyEnrollment } = params;

  switch (currentStep) {
    case FLOW_STEPS.EVENT_SIGNUP_START:
      return FLOW_STEPS.EVENT_EMAIL_SIGNUP;

    case FLOW_STEPS.EVENT_EMAIL_SIGNUP:
    case FLOW_STEPS.EVENT_EMAIL_LOGIN:
      if (showLoyaltyEnrollment) {
        return FLOW_STEPS.EVENT_LOYALTY_ENROLL;
      }
      return FLOW_STEPS.EVENT_SIGNUP_END;

    case FLOW_STEPS.EVENT_EMAIL_RESET:
      return FLOW_STEPS.EVENT_EMAIL_LOGIN;

    case FLOW_STEPS.EVENT_LOYALTY_ENROLL:
      return FLOW_STEPS.EVENT_SIGNUP_END;

    default:
      return null;
  }
}
